import { PrinterIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { useState } from "react";
import "./LabelPrintModal.css";

export const LABEL_SIZES = [
  { value: "40x20", label: "40x20", widthMm: 40, heightMm: 20, gapMm: 2 },
  { value: "40x20x3", label: "40x20x3", widthMm: 40, heightMm: 20, gapMm: 3 },
  { value: "40x20x3zebra", label: "40x20x3zebra", widthMm: 40, heightMm: 20, gapMm: 3 },
  { value: "40x20x3godex", label: "40x20x3godex", widthMm: 40, heightMm: 20, gapMm: 3 },
  { value: "nfs-40x20", label: "Not For Sale (40x20)", widthMm: 40, heightMm: 20, gapMm: 2 },
  { value: "100x50", label: "100x50", widthMm: 100, heightMm: 50, gapMm: 2 },
  { value: "80x35", label: "80x35", widthMm: 80, heightMm: 35, gapMm: 2 },
  { value: "50x40", label: "50x40", widthMm: 50, heightMm: 40, gapMm: 2 },
  { value: "50x50", label: "50x50", widthMm: 50, heightMm: 50, gapMm: 2 },
  { value: "40x25", label: "40x25", widthMm: 40, heightMm: 25, gapMm: 2 },
  { value: "50x25", label: "50x25", widthMm: 50, heightMm: 25, gapMm: 2 },
];

const LABELS_PER_ROW_OPTIONS = [
  { value: 1, label: "1 per row" },
  { value: 2, label: "2 per row" },
];

const DEFAULT_SIZE = LABEL_SIZES.find((s) => s.value === "100x50");
const DEFAULT_LABELS_PER_ROW = 1;

const LabelPrintModal = ({ open, onClose, order, items, onPrint, isPrinting }) => {
  const [selectedSize, setSelectedSize] = useState(DEFAULT_SIZE);
  const [labelsPerRow, setLabelsPerRow] = useState(DEFAULT_LABELS_PER_ROW);
  const [dropdownOpen, setDropdownOpen] = useState(false);

  if (!open) return null;

  const totalLabels = (items || []).reduce((sum, item) => sum + (item.quantity || 1), 0);
  // Physical strips that will actually feed through the printer
  const totalStrips = labelsPerRow === 2 ? Math.ceil(totalLabels / 2) : totalLabels;

  // Scale label shape for preview — if 2-up, show both columns side by side
  const MAX_W = 260;
  const MAX_H = 140;
  const previewTotalWidthMm =
    selectedSize.widthMm * labelsPerRow + selectedSize.gapMm * (labelsPerRow - 1);
  const scale = Math.min(MAX_W / previewTotalWidthMm, MAX_H / selectedSize.heightMm);
  const previewW = Math.round(previewTotalWidthMm * scale);
  const previewH = Math.round(selectedSize.heightMm * scale);
  const singleColPreviewW = Math.round(selectedSize.widthMm * scale);

  const firstItem = items?.[0];

  const handlePrint = () => {
    onPrint({
      items,
      customerName: order?.customerName || "Walk-in",
      token: order?.token || "",
      labelWidthMm: selectedSize.widthMm,
      labelHeightMm: selectedSize.heightMm,
      gapMm: selectedSize.gapMm,
      labelsPerRow,
    });
  };

  return (
    <div className="lpm-overlay" onClick={onClose}>
      <div className="lpm-box" onClick={(e) => e.stopPropagation()}>
        {/* ── Header ── */}
        <div className="lpm-header">
          <p className="lpm-header-text">
            Print labels for:&nbsp;
            <span className="lpm-hl">{order?.customerName || "Walk-in"}</span>
            &nbsp;|&nbsp;
            <span className="lpm-hl">Order: #{order?.externalOrderId || order?.orderId}</span>
            &nbsp;|&nbsp;
            <span className="lpm-hl">
              {items?.length || 0} item{(items?.length || 0) !== 1 ? "s" : ""}
            </span>
          </p>
          <button className="lpm-close-btn" onClick={onClose}>
            <XMarkIcon style={{ width: 18, height: 18 }} />
          </button>
        </div>

        {/* ── Body ── */}
        <div className="lpm-body">
          {/* Left: label size + labels per row selectors */}
          <div className="lpm-left">
            <p className="lpm-section-label">Label Print Type</p>

            {/* Size dropdown */}
            <div className="lpm-select-wrapper">
              <div className="lpm-select-trigger" onClick={() => setDropdownOpen((p) => !p)}>
                <span className="lpm-select-value">{selectedSize.label}</span>
                <div className="lpm-select-icons">
                  <button
                    className="lpm-icon-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedSize(DEFAULT_SIZE);
                      setDropdownOpen(false);
                    }}
                  >
                    ✕
                  </button>
                  <span className="lpm-divider" />
                  <button className="lpm-icon-btn lpm-chevron">▾</button>
                </div>
              </div>

              {dropdownOpen && (
                <div className="lpm-dropdown">
                  {LABEL_SIZES.map((size) => (
                    <div
                      key={size.value}
                      className={`lpm-dropdown-item${
                        selectedSize.value === size.value ? " lpm-dropdown-item--active" : ""
                      }`}
                      onClick={() => {
                        setSelectedSize(size);
                        setDropdownOpen(false);
                      }}
                    >
                      {size.label}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Labels per row toggle */}
            <p className="lpm-section-label" style={{ marginTop: 16 }}>
              Labels Per Row
            </p>
            <div style={{ display: "flex", gap: 8, marginTop: 4 }}>
              {LABELS_PER_ROW_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  onClick={() => setLabelsPerRow(opt.value)}
                  style={{
                    flex: 1,
                    padding: "8px 0",
                    borderRadius: 6,
                    border: labelsPerRow === opt.value ? "2px solid #2563eb" : "2px solid #d1d5db",
                    background: labelsPerRow === opt.value ? "#eff6ff" : "#fff",
                    color: labelsPerRow === opt.value ? "#2563eb" : "#374151",
                    fontWeight: labelsPerRow === opt.value ? 700 : 400,
                    cursor: "pointer",
                    fontSize: 13,
                    transition: "all 0.15s",
                  }}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {/* Right: preview + details */}
          <div className="lpm-right">
            <div className="lpm-preview-card">
              <p className="lpm-section-label" style={{ textAlign: "center", marginBottom: 12 }}>
                Sample Label Layout
              </p>

              <div className="lpm-preview-area">
                {/* Preview shows 1 or 2 columns depending on selection */}
                <div
                  className="lpm-preview-label"
                  style={{
                    width: previewW,
                    height: previewH,
                    display: "flex",
                    gap: Math.round(selectedSize.gapMm * scale),
                    padding: 0,
                    overflow: "hidden",
                  }}
                >
                  {Array.from({ length: labelsPerRow }).map((_, colIdx) => (
                    <div
                      key={colIdx}
                      style={{
                        width: singleColPreviewW,
                        height: "100%",
                        borderRight: colIdx < labelsPerRow - 1 ? "1px dashed #cbd5e1" : "none",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                      }}
                    >
                      <div className="lpm-preview-content">
                        <span className="lpm-preview-customer">
                          {order?.customerName || "Walk-in"}
                        </span>
                        <span className="lpm-preview-divider">--------------------</span>
                        <span className="lpm-preview-product">
                          {firstItem?.productName || "—"}
                        </span>
                        {firstItem?.variantName && (
                          <span className="lpm-preview-variant">{firstItem.variantName}</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="lpm-details">
              <p className="lpm-section-label" style={{ marginBottom: 8 }}>
                Print Details
              </p>
              <div className="lpm-detail-row">
                <span>Labels To Print:</span>
                <span className="lpm-detail-val">{totalLabels}</span>
              </div>
              <div className="lpm-detail-row">
                <span>Labels Per Row:</span>
                <span className="lpm-detail-val">{labelsPerRow}</span>
              </div>
              <div className="lpm-detail-row">
                <span>Physical Strips:</span>
                <span className="lpm-detail-val">{totalStrips}</span>
              </div>
              <div className="lpm-detail-row">
                <span>Each Label Size:</span>
                <span className="lpm-detail-val">{selectedSize.label}</span>
              </div>
            </div>
          </div>
        </div>

        {/* ── Footer ── */}
        <div className="lpm-footer">
          <button
            className="lpm-print-btn"
            onClick={handlePrint}
            disabled={isPrinting || !items?.length}
          >
            {isPrinting ? "Printing…" : "Print"}
            <PrinterIcon style={{ width: 16, height: 16, marginLeft: 6 }} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default LabelPrintModal;
